import { useEffect, useState } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HiOutlineSquares2X2, HiOutlineTrophy, HiOutlineBuildingLibrary, HiOutlineSwatch, HiOutlineCalendar, HiOutlineBuildingOffice2, HiOutlineChatBubbleLeftEllipsis, HiOutlineLightBulb } from 'react-icons/hi2'
import { hasMetricsConsent } from "./common/CookieConsentBanner"

const API_URL = import.meta.env.VITE_API_URL

function PaintingList() {
  const { t, i18n } = useTranslation()
  const { type: paramType, id } = useParams()
  const location = useLocation()
  let type = paramType
  if (!type) {
    if (location.pathname.startsWith('/colors/')) type = 'colors';
    else if (location.pathname.startsWith('/awards/')) type = 'awards';
    else if (location.pathname.startsWith('/series/')) type = 'series';
    else if (location.pathname.startsWith('/exhibitions/')) type = 'exhibitions';
  }
  const [meta, setMeta] = useState(null)
  const [paintings, setPaintings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
    setLoading(true)
    setError(null)
      try {
    let metaUrl = ''
    if (type === 'series') {
      metaUrl = `${API_URL}/series/${id}`
    } else if (type === 'exhibitions') {
      metaUrl = `${API_URL}/exhibitions/${id}`
    } else if (type === 'awards') {
      metaUrl = `${API_URL}/awards/${id}`
    } else if (type === 'colors') {
      metaUrl = `${API_URL}/colors/${id}`
        }

        console.log('[PaintingList] Fetching meta:', metaUrl)
        const metaResponse = await fetch(metaUrl)
        console.log('[PaintingList] Meta response:', metaResponse)
        if (!metaResponse.ok) {
          throw new Error(`HTTP error! status: ${metaResponse.status} for ${metaUrl}`)
        }
        const metaData = await metaResponse.json()
        setMeta(metaData)
        console.log('[PaintingList] Meta data:', metaData)

        const paintingsUrl = `${API_URL}/paintings`
        console.log('[PaintingList] Fetching paintings:', paintingsUrl)
        const paintingsResponse = await fetch(paintingsUrl)
        console.log('[PaintingList] Paintings response:', paintingsResponse)
        if (!paintingsResponse.ok) {
          throw new Error(`HTTP error! status: ${paintingsResponse.status} for ${paintingsUrl}`)
        }
        const paintingsData = await paintingsResponse.json()
        console.log('[PaintingList] Paintings data:', paintingsData)
        
        let filteredPaintings = []
        if (Array.isArray(paintingsData)) {
          if (type === 'series') {
            filteredPaintings = paintingsData.filter(p => p.serie_id == id)
          } else if (type === 'exhibitions') {
            filteredPaintings = paintingsData.filter(p => Array.isArray(p.exhibition_ids) && p.exhibition_ids.includes(Number(id)))
          } else if (type === 'awards') {
            filteredPaintings = paintingsData.filter(p => Array.isArray(p.award_ids) && p.award_ids.includes(Number(id)))
          } else if (type === 'colors') {
            filteredPaintings = paintingsData.filter(p => Array.isArray(p.color_ids) && p.color_ids.includes(Number(id)))
          }
        }
        setPaintings(filteredPaintings)
        console.log('[PaintingList] Filtered paintings:', filteredPaintings)
      } catch (err) {
        console.error('[PaintingList] Error fetching data:', err, { type, id })
        setError(`${err.message} (type: ${type}, id: ${id})`)
      } finally {
        setLoading(false)
      }
    }

    if (type && id) {
      fetchData()
    }
  }, [type, id])

  // Tracking: Meta-Detail-View (Color, Award, Exhibition, Series)
  useEffect(() => {
    if (hasMetricsConsent() && meta && id && type) {
      // Map plural to singular for entity_type
      let entityType = type;
      if (entityType === 'colors') entityType = 'color';
      if (entityType === 'awards') entityType = 'award';
      if (entityType === 'exhibitions') entityType = 'exhibition';
      if (entityType === 'series') entityType = 'series';
      fetch(`${API_URL}/track-view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Metrics-Consent": "true"
        },
        body: JSON.stringify({ entity_type: entityType, entity_id: Number(id) })
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.id, id, type]);

  if (loading) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100"></div>
    </main>
  )

  if (error) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="text-center text-red-500 dark:text-red-400 p-8">
        {t('error.loading')}
      </div>
    </main>
  )

  // Hilfsfunktion für Datumsformatierung
  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  // Überschrift und Beschreibung
  let title = ''
  let description = ''
  let Icon = null
  if (type === 'series') {
    title = meta?.name || meta?.title || t('series.title')
    description = i18n.language === 'de' ? meta?.description_de : meta?.description_en || ''
    Icon = HiOutlineSquares2X2
  } else if (type === 'exhibitions') {
    title = meta?.name || meta?.title || t('exhibitions.title')
    description = meta?.description || ''
    Icon = HiOutlineBuildingLibrary
  } else if (type === 'awards') {
    title = meta?.name || meta?.title || t('awards.title')
    description = meta?.description || ''
    Icon = HiOutlineTrophy
  } else if (type === 'colors') {
    title = i18n.language === 'de' ? meta?.name_de : meta?.name_en || t('colors.title')
    description = ''
    Icon = HiOutlineSwatch
  }

  return (
    <div className="min-h-screen w-full bg-gray-100 dark:bg-zinc-900 transition-colors duration-300">
      {/* Header */}
      <section className="w-screen py-12 px-4 md:px-12 lg:px-24 text-center bg-white dark:bg-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-3">
            {Icon && <Icon className={
              type === 'series' ? 'text-violet-500 text-3xl' :
              type === 'awards' ? 'text-yellow-500 text-3xl' :
              type === 'exhibitions' ? 'text-blue-500 text-3xl' :
              type === 'colors' ? 'text-pink-500 text-3xl' :
              'text-gray-500 text-3xl'
            } />}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white">
              {title}
            </h1>
          </div>
          {/* Zusatzinfos je nach Typ */}
          {type === 'colors' && meta && (
            <div className="flex flex-col items-center mb-6">
              <div 
                className="w-20 h-20 rounded-lg border-2 shadow mb-2" 
                style={{ 
                  background: meta.name_de === 'Divers' || meta.name_en === 'Diverse'
                    ? 'linear-gradient(45deg, #ff6b6b, #ffd93d, #6c5ce7, #4ecdc4, #45b7d1, #96ceb4, #ffeead, #ff9a9e, #a8e6cf, #dcedc1)'
                    : meta.color_code,
                  borderColor: meta.name_de === 'Divers' || meta.name_en === 'Diverse'
                    ? '#e5e7eb'
                    : meta.color_code
                }} 
              />
            </div>
          )}
          {type === 'series' && meta && meta.cover_image && (
            <div className="flex justify-center mb-6">
              <img src={meta.cover_image} alt={title} className="w-40 h-40 object-cover rounded-xl shadow" />
            </div>
          )}
          {type === 'exhibitions' && meta && (
            <div className="flex flex-col items-center mb-6 text-sm text-gray-700 dark:text-gray-300 gap-1">
              {(meta.start_date || meta.end_date) && (
                <div className="flex items-center gap-2"><HiOutlineCalendar />{formatDate(meta.start_date)}{meta.end_date ? ` – ${formatDate(meta.end_date)}` : ''}</div>
              )}
              {meta.location && <div className="flex items-center gap-2"><HiOutlineBuildingLibrary />{meta.location}</div>}
              {meta.organizer && <div className="flex items-center gap-2"><HiOutlineBuildingOffice2 />{meta.organizer}</div>}
              {meta.concept && <blockquote className="italic mt-2 flex items-center gap-2"><HiOutlineLightBulb />"{meta.concept}"</blockquote>}
            </div>
          )}
          {type === 'awards' && meta && (
            <div className="flex flex-col items-center mb-6 text-sm text-gray-700 dark:text-gray-300 gap-1">
              {meta.date && <div className="flex items-center gap-2"><HiOutlineCalendar />{formatDate(meta.date)}</div>}
              {meta.location && <div className="flex items-center gap-2"><HiOutlineBuildingLibrary />{meta.location}</div>}
              {meta.institution && <div className="flex items-center gap-2"><HiOutlineBuildingOffice2 />{meta.institution}</div>}
              {meta.jury_statement && <blockquote className="italic mt-2 flex items-center gap-2"><HiOutlineChatBubbleLeftEllipsis />"{meta.jury_statement}"</blockquote>}
            </div>
          )}
          {description && (
            <div className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mt-6 whitespace-pre-line">
              {description}
            </div>
          )}
        </div>
      </section>
      {/* Paintings Grid */}
      <main className="px-4 md:px-12 lg:px-24 pb-12">
        <div className="max-w-7xl mx-auto">
          {paintings.length === 0 ? (
            <div className="text-center p-8">
              <p className="text-gray-500 mb-4 text-lg dark:text-gray-400">{t("no_paintings")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              {paintings.map((p) => {
                let mainImg = Array.isArray(p.images)
                  ? p.images.find(img => img.is_main === 1 || img.is_main === '1' || img.is_main === true) || p.images[0]
                  : null;
                return (
                  <Link
                    key={p.id}
                    to={`/gallery/${p.id}`}
                    className="bg-white/80 dark:bg-zinc-800/80 border rounded-lg shadow-lg overflow-hidden flex flex-col hover:scale-105 hover:shadow-2xl transition-transform duration-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                    style={{ textDecoration: 'none' }}
                  >
                    {mainImg && (
                      <div className="relative group">
                        <img
                          src={`${API_URL}${mainImg.thumb_path || mainImg.file_path}`}
                          alt={p.name}
                          className="w-full h-64 md:h-72 lg:h-80 object-cover group-hover:brightness-90 transition duration-300"
                          loading="lazy"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <span className="text-white text-sm md:text-base">{p.name}</span>
                        </div>
                      </div>
                    )}
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="font-bold text-xl md:text-2xl mb-1 text-gray-900 dark:text-gray-100">
                        {p.name}
                      </h3>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default PaintingList 